import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/app/dialog/dialog";
import { profileVerified } from "assets";

import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { AppLog } from "services";
import { RouteUrl, StatusMsg } from "utils";
import { ExternalLink, SendHorizontalIcon, Sticker, X } from "lucide-react";
import { FaRegCommentAlt } from "react-icons/fa";
import { Button } from "components/ui/button";
import { PiShareFatLight } from "react-icons/pi";
import LinkifyText from "components/app/linkify/LinkifyText";
import ButtonFollow from "components/app/button-follow/ButtonFollow";
import ImageUrlProfile from "components/app/image-url/ImageUrlProfile";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TextReaction from "pages/profile-post-contents-page-view/post-component/TextReaction";
import NewFeedSingleImage from "components/app/new-feed-post/new-feed-components/new-feed-post-image-templates/NewFeedSignleImage";
import NewFeedPostImagesDefault from "components/app/new-feed-post/new-feed-components/new-feed-post-image-templates/NewFeedPostImagesDefault";
import NewFeedPostImagesColumn from "components/app/new-feed-post/new-feed-components/new-feed-post-image-templates/NewFeedPostImagesColumn";
import NewFeedPostImagesBanner from "components/app/new-feed-post/new-feed-components/new-feed-post-image-templates/NewFeedPostImagesBanner";
import { useInView } from "react-intersection-observer";
import NewFeedPostStatus from "components/app/new-feed-post/new-feed-components/new-feed-post-status/NewFeedPostStatus";
import { postedTimeAgo } from "utils/formatTimeSecondToHour";
import NewFeedLinkButton from "components/app/new-feed-post/new-feed-components/new-feed-link-button/NewFeedLinkButton";
import { faFaceSmileBeam, faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as outlineHeart } from "@fortawesome/free-regular-svg-icons";
import { PostReaction } from "models/postReaction";
import DialogPostReaction from "../DialogPostReaction";
import NewFeedDailogShare from "components/app/new-feed-post/new-feed-components/new-feed-dialogs/NewFeedDialogShare";
import { Link } from "react-router-dom";
import Comment from "pages/profile-post-contents-page-view/post-component/Comment";
import { ScrollArea } from "components/ui/scroll-area";
import { useSocket } from "services/SocketContext";
import { Service } from "utils/services";
import { Textarea } from "components/ui/textarea";
import EmojiPicker, { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import StickerPicker from "pages/profile-post-contents-page-view/post-component/Stickers";
import TotalCommentReaction from "pages/profile-post-contents-page-view/post-component/CommentReaction";
import NewFeedDialogPostVideoPlayer from "components/app/new-feed-post/new-feed-components/new-feed-video-player/NewFeedDialogPostVideoPlayer";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { useDialogNewFeedPostStore } from "store/dialog/useDialogNewFeedPostStore";

interface DialogProps {
  postProp: any;
  isDialogOpen: boolean;
  setIsDialogOpen: (value: boolean, videoCurrentTime: number, videoIsPlaying: boolean, isOpenPostModal: boolean) => void;
  videoCurrentTime: number;
  videoIsPlaying: boolean;
  onOpenPostModal: (postId: string) => void;
}

export default function DialogNewFeedPost({ postProp, isDialogOpen, setIsDialogOpen, videoCurrentTime = 0, videoIsPlaying, onOpenPostModal }: DialogProps) {
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const { ref: inViewRef, inView } = useInView({ threshold: 0.6 });

  const {
    post,
    postAppRecord,
    comments,
    commentAppRecord,
    initializePage,
    onClickedFollow,
    onClickedLike,
    onClickedShare,
    fetchComments,
    fetchSubComments,
    requestReactionComment,
    requestReplyComment,
    requestSubmitComment,

    disposePage,
  } = useDialogNewFeedPostStore();

  const [dialogPostReaction, setDialogPostReaction] = useState({
    isOpen: false,
    post: null,
    postReaction: PostReaction.like,
  });

  const [dialogShare, setDialogShare] = useState({
    isOpen: false,
    post: null,
  });

  // * ==== Comment ==== * //
  const { joinRoom, leaveRoom } = useSocket();
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [textComment, setTextComment] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [sticker, setSticker] = useState<any>({});
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const stickerPickerRef = useRef<HTMLDivElement>(null);

  // * ==== ====== ==== * //
  const onOpenReactionDialog = (post: any, postReaction: PostReaction) => {
    try {
      setDialogPostReaction({
        isOpen: true,
        post: post,
        postReaction,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenReactionDialog]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const onOpenDialogShare = (post: any) => {
    try {
      setDialogShare({
        isOpen: true,
        post: post,
      });
    } catch (error) {
      AppLog.show({
        message: `[onOpenDialogShare]`,
        status: StatusMsg.error,
        error,
      });
    }
  };

  const onGoToPost = (postId: string) => {
    setIsDialogOpen(false, videoRef?.current?.currentTime ?? 0, videoRef?.current ? !videoRef?.current?.paused : false, true);
    onOpenPostModal(postId);
  };

  // * ==== Comment ==== * //

  const handleReplyClick = (commentId: number) => {
    setReplyingTo(commentId);
  };

  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (textComment) {
        requestSubmitComment(sticker, textComment);
        setTextComment("");
        setSticker({});
      }
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    setTextComment((prev) => prev + emojiData.emoji);
    // setShowEmojiPicker(false);
  };

  useEffect(() => {
    if (inView && commentAppRecord.canLoadMore()) {
      fetchComments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  useEffect(() => {
    if (isDialogOpen === false) {
      disposePage();
      if (postProp) {
        leaveRoom(`${postProp?.provider_post_id}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disposePage, isDialogOpen]);

  useEffect(() => {
    const loadInitialReactions = async () => {
      try {
        initializePage(postProp);
        if (postProp) {
          joinRoom(`${postProp?.provider_post_id}`);
        }
      } catch (error) {
        AppLog.show({
          message: `[loadInitialReactions]`,
          status: StatusMsg.error,
          error,
        });
      }
    };

    if (postProp && isDialogOpen) {
      loadInitialReactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializePage, isDialogOpen, postProp]);

  const _renderContent = () => (
    <div className="mt-3">
      {post.content && <p className="text-muted-foreground font-medium mb-3">{post.content}</p>}

      {post.media_type?.toLowerCase() === "photo" && post.post_attachments?.[0]?.attachment_media_image_file_src && <NewFeedSingleImage post={post} onOpenPostModal={onGoToPost} />}

      {post.media_type?.toLowerCase() === "album" &&
        (post.media_template === "Default" ? (
          <NewFeedPostImagesDefault post={post} images={post.post_attachments.map((att: any) => att.attachment_media_image_file_src)} onOpenPostModal={onGoToPost} />
        ) : post.media_template === "Column" ? (
          <NewFeedPostImagesColumn post={post} images={post.post_attachments.map((att: any) => att.attachment_media_image_file_src)} onOpenPostModal={onGoToPost} />
        ) : (
          <NewFeedPostImagesBanner post={post} images={post.post_attachments.map((att: any) => att.attachment_media_image_file_src)} onOpenPostModal={onGoToPost} />
        ))}

      {post.media_type?.toLowerCase() === "video" && (
        <NewFeedDialogPostVideoPlayer
          videoCurrentTime={videoCurrentTime}
          videoIsPlaying={videoIsPlaying}
          src={post.post_attachments?.[0]?.attachment_media_image_file_src}
          thubnail={post?.post_attachments[0]?.attachment_thumbnail_image_file_src}
          attachment={post.post_attachments?.[0]}
          videoRefCallback={(el) => {
            videoRef.current = el;
            inViewRef(el);
          }}
        />
      )}

      {post.media_type?.toLowerCase() === "status" && <NewFeedPostStatus post={post} />}
    </div>
  );

  return (
    <Dialog open={isDialogOpen}>
      {postAppRecord.isLoading() ? (
        <>loading dd</>
      ) : (
        <DialogContent isShowBtnXClose={false} className="max-w-full lg:max-w-[700px] h-[calc(100vh-80px)] py-2 px-0 flex flex-col">
          <DialogHeader>
            <DialogTitle>
              <div className="flex flex-row items-start space-x-2 w-full pl-3 pt-2">
                <ImageUrlProfile user={post.re_post_user} style={{ width: 50, height: 50 }} className="rounded-full object-cover border shadow-md " isLinkProfile={true} />
                <div className="flex flex-row justify-between items-center w-full">
                  <div className="flex flex-col">
                    <div className="flex items-center space-x-2">
                      <Link to={`${RouteUrl.profile}?id=${post?.re_post_user?.id}`} className="text-lg font-bold hover:underline">
                        {post?.re_post_user?.name}
                      </Link>

                      {post.re_post_user?.is_verified === "Yes" && <img src={profileVerified} alt="" style={{ width: 18, height: 18 }} />}

                      <ButtonFollow className="h-[25px]" friendStatus={post.re_post_user?.friend_status} onClick={() => onClickedFollow(post, post.re_post_user?.id.toString())} />
                    </div>
                    <div className="flex items-center space-x-2">
                      <p className="text-xs text-muted-foreground">
                        {post?.is_sponsored === "Yes"
                          ? "Sponsored"
                          : post?.new_feed_post_type === "suggestion"
                          ? "Suggestion for you"
                          : `${postedTimeAgo(post?.provider_created_datetime)}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </DialogTitle>

            <div
              className="absolute flex items-center justify-center right-[50px] top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
              onClick={() => onGoToPost(post?.provider_post_id)}
            >
              <ExternalLink />
            </div>
            <div
              className="absolute flex items-center justify-center right-3 top-1 z-40 cursor-pointer rounded-full hover:bg-accent hover:text-accent-foreground w-[36px] h-[36px] p-2"
              onClick={() => setIsDialogOpen(false, videoRef?.current?.currentTime ?? 0, videoRef?.current ? !videoRef?.current?.paused : false, false)}
            >
              <X />
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 items-center">
            <div className=" px-3 pb-1 rounded-lg  mb-4 w-full ">
              <div>
                {post.media_title && (
                  <div className="mt-2">
                    <LinkifyText text={post.media_title} target="_blank" />
                  </div>
                )}
                {post.re_post_user ? <></> : post.re_post.is_deleted === 1 ? <h1>The content is unavailable!</h1> : <></>}

                <div className="-mx-3">{_renderContent()}</div>

                <NewFeedLinkButton post={post} />

                {/* total like comment share */}
                <div className="py-2">
                  <TextReaction post={post} onOpenReactionDialog={onOpenReactionDialog} />
                </div>

                <div className="flex justify-between py-1 border-y border-gray-300">
                  <Button variant="ghost" className="space-x-2 flex-1 " onClick={(e: React.MouseEvent) => onClickedLike(post)}>
                    <FontAwesomeIcon
                      icon={`${post?.is_reaction_like}` === "1" ? solidHeart : outlineHeart}
                      size="lg"
                      className={`${post?.is_reaction_like}` === "1" ? "text-red-600" : "text-gray-600"}
                    />
                    <p>{t("Love")}</p>
                  </Button>

                  <Button
                    variant="ghost"
                    className="space-x-2 flex-1"
                    // onClick={(e: React.MouseEvent) => onOpenDialogNewFeedPost(post)}
                  >
                    <FaRegCommentAlt className="text-lg" />
                    <p>{t("Comment")}</p>
                  </Button>

                  <Button variant="ghost" className="space-x-2 flex-1" onClick={(e: React.MouseEvent) => onOpenDialogShare(post)}>
                    <PiShareFatLight className="text-lg" />
                    <p>{t("Share")}</p>
                  </Button>
                </div>
                <div className="py-2">
                  <TotalCommentReaction post={post} onOpenReactionDialog={onOpenReactionDialog} />
                </div>
              </div>
            </div>

            <div className="space-y-2 px-2">
              {[...comments].map((comment, index) => (
                <Comment
                  replyingTo={replyingTo!}
                  onReplyClick={() => handleReplyClick(comment.id)}
                  key={comment.id}
                  comment={comment}
                  index={index}
                  submitReply={(data: any) => {
                    requestReplyComment(data);
                    handleReplyClick(-1);
                  }}
                  onClikcReact={() => requestReactionComment({ comment })}
                  fetchSubComments={fetchSubComments}
                >
                  {comment.sub_comments?.data?.length > 0 && (
                    <div className="">
                      {comment.sub_comments.data.map((subComment: any, subIndex: number) => (
                        <Comment
                          replyingTo={replyingTo!}
                          onReplyClick={() => handleReplyClick(subComment.id)}
                          key={subComment.id}
                          comment={subComment}
                          index={subIndex}
                          submitReply={(data: any) => {
                            requestReplyComment(data);
                            handleReplyClick(-1);
                          }}
                          onClikcReact={() =>
                            requestReactionComment({
                              comment: subComment,
                            })
                          }
                          fetchSubComments={fetchSubComments}
                          isSubComment={true}
                        >
                          {/* Recursively render deeper sub-comments */}
                          {subComment.sub_comments?.data?.length > 0 && (
                            <div className="">
                              {subComment.sub_comments.data.map((nestedComment: any, nestedIndex: number) => (
                                <Comment
                                  replyingTo={replyingTo!}
                                  onReplyClick={() => handleReplyClick(nestedComment.id)}
                                  key={nestedComment.id}
                                  comment={nestedComment}
                                  index={nestedIndex}
                                  submitReply={(data: any) => {
                                    requestReplyComment(data);
                                    handleReplyClick(-1);
                                  }}
                                  onClikcReact={() =>
                                    requestReactionComment({
                                      comment: nestedComment,
                                    })
                                  }
                                  fetchSubComments={fetchSubComments}
                                  isSubComment={true}
                                >
                                  {nestedComment.sub_comments?.data?.length > 0 && (
                                    <div className="">
                                      {nestedComment.sub_comments.data.map((nestedComment_f: any, nestedIndex_f: number) => (
                                        <Comment
                                          replyingTo={replyingTo!}
                                          onReplyClick={() => handleReplyClick(nestedComment_f.id)}
                                          key={nestedComment_f.id}
                                          comment={nestedComment_f}
                                          index={nestedIndex_f}
                                          submitReply={(data: any) => {
                                            requestReplyComment(data);
                                            handleReplyClick(-1);
                                          }}
                                          onClikcReact={() =>
                                            requestReactionComment({
                                              comment: nestedComment_f,
                                            })
                                          }
                                          isSubComment={true}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </Comment>
                              ))}
                            </div>
                          )}
                        </Comment>
                      ))}
                    </div>
                  )}
                </Comment>
              ))}

              <div ref={inViewRef} className="h-10 flex justify-center items-center"></div>
            </div>
          </ScrollArea>

          <div className="flex flex-row  dark:bg-background  justify-center items-start space-x-2 w-full pt-2 mt-4 px-4">
            <ImageUrlProfile isLinkProfile={true} user={Service?.userAuth} className="cursor-pointer rounded-full w-7 h-7" alt="logo" />
            <div className="flex flex-col space-y-2  w-full items-start justify-start">
              <div className="border  relative p-2 rounded-2xl w-full dark:bg-[#3A3B3C] ">
                <div className="flex flex-col">
                  <Textarea
                    value={textComment}
                    onKeyDown={onHandleKeyDown}
                    maxLength={100}
                    onChange={(e) => setTextComment(e.target.value)}
                    className="border border-none min-h-[10px]  focus-visible:ring-0 focus-visible:ring-offset-0 w-full  px-0 py-0 rounded-none dark:bg-[#3A3B3C]"
                    placeholder="Add comment"
                  />
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-2 items-center z-50">
                      <Popover>
                        <PopoverTrigger asChild>
                          <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center">
                            <FontAwesomeIcon
                              icon={faFaceSmileBeam}
                              className="text-gray-700 dark:text-gray-500 cursor-pointer size-5"
                              onClick={() => setShowEmojiPicker((prev) => !prev)}
                            />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-92">
                          <div ref={emojiPickerRef} className="absolute bottom-[0px] left-0 z-[100px]" spellCheck={false}>
                            <EmojiPicker reactionsDefaultOpen={false} onEmojiClick={onEmojiClick} emojiStyle={EmojiStyle.FACEBOOK} searchDisabled={true} skinTonesDisabled={true} />
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Popover>
                        <PopoverTrigger asChild>
                          <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center">
                            <Sticker className=" text-gray-700 dark:text-gray-500 font-extrabold cursor-pointer size-5" onClick={() => setShowStickerPicker(true)} />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-92">
                          {showStickerPicker && (
                            <StickerPicker
                              stickerPickerRef={stickerPickerRef}
                              onStickerSelect={(sticker) => {
                                setShowStickerPicker(false);

                                setSticker(sticker);
                              }}
                            />
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="flex w-8 h-8 rounded-full  bg-gray-200 text-center items-center justify-center">
                      <SendHorizontalIcon
                        className={`cursor-pointer size-5 ${textComment || Object.keys(sticker).length !== 0 ? "text-yellow-400" : "text-gray-700 dark:text-gray-500"}`}
                        onClick={
                          !textComment && Object.keys(sticker).length === 0
                            ? () => {}
                            : () => {
                                requestSubmitComment(sticker, textComment);
                                setTextComment("");
                                setSticker({});
                              }
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
              {Object.keys(sticker).length !== 0 ? (
                <div className="relative sticker-image w-16 h-16">
                  <img src={sticker.thumbnail_url} alt={sticker.pack_name} />
                  <div
                    className="absolute bg-gray-200 hover:bg-gray-300 -top-1 -right-2 w-5 h-5 rounded-full dark:bg-[#3A3B3C] flex items-center cursor-pointer justify-center hover:dark:bg-[#4E4F50]"
                    onClick={() => setSticker({})}
                  >
                    <X className="w-4 h-4 " />
                  </div>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
        </DialogContent>
      )}

      {dialogPostReaction.isOpen && (
        <DialogPostReaction
          post={dialogPostReaction.post}
          isDialogOpen={dialogPostReaction.isOpen}
          postReaction={dialogPostReaction.postReaction}
          setIsDialogOpen={(isOpen) => setDialogPostReaction({ ...dialogPostReaction, isOpen })}
        />
      )}

      {dialogShare.isOpen && (
        <NewFeedDailogShare
          // setIsDialogOpen={setIsDialogOpen}
          // videoCurrentTime={videoCurrentTime}
          // videoIsPlaying={videoIsPlaying}
          post={dialogShare.post}
          onSuccess={(post) => onClickedShare(post)}
          isDialogOpen={dialogShare.isOpen}
          setIsDialogOpen={(isOpen) => setDialogShare({ ...dialogShare, isOpen })}
        />
      )}
    </Dialog>
  );
}

